import type { ComponentProps } from "react";
import { Link } from "react-router-dom";
import type { Components } from "react-markdown";
import { resolveHelpImage } from "./help-images";
import { HelpMark } from "./HelpMark";
import { HelpExampleCard } from "./HelpExampleCard";
import { HelpExampleStrategyPage } from "./HelpExampleStrategyPage";
import { HelpExamplePortfolioBuilder } from "./HelpExamplePortfolioBuilder";
import { HelpExamplePortfolioTracker } from "./HelpExamplePortfolioTracker";

/**
 * The component map is where a help article stops looking like raw Markdown
 * and starts looking like the portal.
 *
 * Headings keep their semantic element and are scaled down in custom.scss —
 * rendering `##` as an `h5` to get the right size would break the document
 * outline for screen readers. rehype-slug supplies the ids; the anchor here
 * makes them linkable, which is what lets a strategy tooltip point at a
 * specific section rather than the top of the page.
 */

// A render helper rather than a component: this file's only export is the
// component map, and mixing a component export in breaks fast refresh.
function headingAnchor(id: string | undefined) {
  if (!id) return null;
  return (
    <a
      href={`#${id}`}
      className="help-anchor text-decoration-none"
      aria-label="Link to this section"
    >
      #
    </a>
  );
}

function isInternal(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

/**
 * `help-mark` and the `help-example-*` elements are not HTML, so they are not in
 * react-markdown's Components map and have to be merged in past the type. The
 * element names come from remarkMarks and remarkExampleCard, and nothing else
 * emits them.
 */
const customComponents = {
  "help-mark": ({
    "data-mark": type,
    "data-label": label,
  }: {
    "data-mark"?: string;
    "data-label"?: string;
  }) => <HelpMark type={type} label={label} />,

  "help-example-card": () => <HelpExampleCard />,

  "help-example-strategy-page": () => <HelpExampleStrategyPage />,

  "help-example-portfolio-builder": () => <HelpExamplePortfolioBuilder />,

  "help-example-portfolio-tracker": () => <HelpExamplePortfolioTracker />,
};

const htmlComponents: Components = {
  h2: ({ id, children }) => (
    <h2 id={id}>
      {children}
      {headingAnchor(id)}
    </h2>
  ),

  h3: ({ id, children }) => (
    <h3 id={id}>
      {children}
      {headingAnchor(id)}
    </h3>
  ),

  a: ({ href, children, ...rest }: ComponentProps<"a">) => {
    if (!href) return <span>{children}</span>;

    // Internal links route client-side; a full page load here would drop the
    // MSAL session state and flash the whole shell.
    if (isInternal(href)) {
      return <Link to={href}>{children}</Link>;
    }

    return (
      <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },

  // Wide tables scroll inside their own container so the page body never
  // scrolls sideways on a narrow viewport.
  table: ({ children }) => (
    <div className="table-responsive mb-3">
      <table className="table table-sm align-middle mb-0">{children}</table>
    </div>
  ),

  // Markdown gives no way to mark an image decorative, and a help screenshot
  // never is, so alt is required — the build rejects an article without it.
  img: ({ src, alt, title, ...rest }: ComponentProps<"img">) => {
    const resolved = typeof src === "string" ? resolveHelpImage(src) : null;
    if (!resolved) return null;

    return (
      <figure className="help-figure">
        <img
          {...rest}
          src={resolved}
          alt={alt ?? ""}
          loading="lazy"
          decoding="async"
          className="img-fluid rounded border"
        />
        {title && <figcaption>{title}</figcaption>}
      </figure>
    );
  },

  blockquote: ({ children }) => (
    <blockquote className="help-quote ps-3 my-3">{children}</blockquote>
  ),
};

export const markdownComponents: Components = {
  ...htmlComponents,
  ...(customComponents as Components),
};
